import axios, { AxiosInstance, AxiosError } from 'axios';

// User Portal Backend URL (not the main API)
console.log('=== API Client Debug ===');
console.log('VITE_USER_PORTAL_API_URL:', import.meta.env.VITE_USER_PORTAL_API_URL);
console.log('VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
// Use empty string if explicitly set (for proxy), otherwise fall back to other values
const API_BASE_URL = import.meta.env.VITE_USER_PORTAL_API_URL !== undefined
  ? import.meta.env.VITE_USER_PORTAL_API_URL
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003');
console.log('Final API_BASE_URL:', API_BASE_URL);
console.log('=======================');

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: Send cookies with requests
});

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // If error is 401, user needs to login again
    if (error.response?.status === 401) {
      // Let AuthContext/ProtectedRoute handle redirect
      // No token refresh needed - backend manages tokens in httpOnly cookies
    }

    return Promise.reject(error);
  }
);

// API client methods
export const api = {
  // Auth - Shared KYC
  auth: {
    initiate: (email: string, countryCode: string, providerId?: string) =>
      apiClient.post('/api/auth/initiate', { email, countryCode, providerId }),

    verify: (email: string, otp: string) =>
      apiClient.post('/api/auth/verify', { email, otp }),

    me: () =>
      apiClient.get('/api/auth/me'),

    logout: () =>
      apiClient.post('/api/auth/logout'),

    revoke: () =>
      apiClient.post('/api/auth/revoke'),
  },

  // Ramp - Shared KYC Stateless
  ramp: {
    getBankNetworks: (countryCode: string = 'NG') =>
      apiClient.get('/api/ramp/bank-networks', { params: { countryCode } }),

    verifyBank: (data: { accountNumber: string; networkId: string; countryCode: string; providerId?: string }) =>
      apiClient.post('/api/ramp/verify-bank', data),

    onramp: (data: {
      amount: number;
      cryptoCurrency: string;
      cryptoNetwork: string;
      walletAddress: string;
      walletTag?: string;
      countryCode: string;
      currency: string;
      providerId?: string;
    }) =>
      apiClient.post('/api/ramp/onramp', data),

    offramp: (data: {
      cryptoAmount: number;
      cryptoCurrency: string;
      cryptoNetwork: string;
      countryCode: string;
      currency: string;
      bankAccount: {
        accountNumber: string;
        accountName?: string;
        networkId: string;
      };
      providerId?: string;
    }) =>
      apiClient.post('/api/ramp/offramp', data),

    checkStatus: (providerRef: string, type?: 'ONRAMP' | 'OFFRAMP') => {
      const url = type
        ? `/api/ramp/status/${providerRef}?type=${type}`
        : `/api/ramp/status/${providerRef}`;
      return apiClient.get(url);
    },

    getLimits: () =>
      apiClient.get('/api/ramp/limits'),
  },

  // KYC
  kyc: {
    submit: (data: { countryCode: string; providerId?: string; vendorId?: string; kycData?: Record<string, string>; documents?: Record<string, string> }) =>
      apiClient.post('/api/kyc/submit', data),
    requirements: (params: { countryCode: string; providerId?: string; vendorId?: string }) =>
      apiClient.get('/api/kyc/requirements', { params }),
    status: (params?: { countryCode?: string; providerId?: string; vendorId?: string }) =>
      apiClient.get('/api/kyc/status', { params }),
    data: (params?: { countryCode?: string; providerId?: string; vendorId?: string }) =>
      apiClient.get('/api/kyc/data', { params }),
  },

  // Crypto
  crypto: {
    channels: () =>
      apiClient.get('/api/crypto/channels'),

    rate: (params: {
      amount: string;
      fromCurrency: string;
      toCurrency: string;
      type: 'BUY' | 'SELL';
      countryCode: string;
    }) =>
      apiClient.get('/api/crypto/rate', { params }),
  },

  // Profile
  profile: {
    get: () =>
      apiClient.get('/api/profile'),
    update: (data: any) =>
      apiClient.put('/api/profile', data),
  },

  // Bank Accounts
  bankAccounts: {
    list: () =>
      apiClient.get('/api/bank-accounts/accounts'),
    add: (data: {
      networkId: string;
      bankName: string;
      accountNumber: string;
      accountName: string;
    }) =>
      apiClient.post('/api/bank-accounts/accounts', data),
    remove: (id: string) =>
      apiClient.delete(`/api/bank-accounts/accounts/${id}`),
  },

  // Wallets
  wallets: {
    list: () =>
      apiClient.get('/api/wallets'),
    add: (data: any) =>
      apiClient.post('/api/wallets', data),
    remove: (id: string) =>
      apiClient.delete(`/api/wallets/${id}`),
  },

  // Transactions
  transactions: {
    list: (params?: any) =>
      apiClient.get('/api/transactions', { params }),
  },

  // Sessions (placeholder - not yet implemented)
  sessions: {
    list: () => Promise.resolve({ data: { sessions: [] } }),
    create: (_data: any) => Promise.resolve({ data: { id: '' } }),
    revoke: (_id: string) => Promise.resolve({ data: null }),
  },

  // Static Addresses
  staticAddresses: {
    list: (params?: { limit?: number; offset?: number }) =>
      apiClient.get('/api/static-addresses', { params }),

    create: (data: {
      bankAccountId: string;
      originNetwork: string;
      originAsset?: string;
      settlementAsset?: string;
      evmRefundAddress?: string;
    }) =>
      apiClient.post('/api/static-addresses', data),

    get: (id: string) =>
      apiClient.get(`/api/static-addresses/${id}`),

    getDeposits: (id: string, params?: { limit?: number; offset?: number }) =>
      apiClient.get(`/api/static-addresses/${id}/deposits`, { params }),

    sync: (id: string) =>
      apiClient.post(`/api/static-addresses/${id}/sync`),

    getSupportedChains: () =>
      apiClient.get('/api/static-addresses/supported-chains'),
  },
};
